import { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { LayoutDashboard, Package, ShoppingCart, Users, BarChart3, Settings, LogOut, Menu, X, PawPrint, List, MapPin } from 'lucide-react';

const navItems = [
    { href: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/admin/productos', label: 'Productos', icon: Package },
    { href: '/admin/categorias', label: 'Categorías', icon: List },
    { href: '/admin/pedidos', label: 'Pedidos', icon: ShoppingCart },
    { href: '/admin/vendedores', label: 'Vendedores', icon: Users },
    { href: '/admin/locales', label: 'Locales', icon: MapPin },
    { href: '/admin/visitas', label: 'Visitas', icon: BarChart3 },
    { href: '/admin/configuracion', label: 'Configuracion', icon: Settings },
];

export default function AdminLayout() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const token = localStorage.getItem('cloudpet-admin-token');
    if (!token) {
        navigate('/admin/login');
        return null;
    }

    const handleLogout = () => {
        localStorage.removeItem('cloudpet-admin-token');
        navigate('/admin/login');
    };

    const isActive = (href) => {
        if (href === '/admin') return location.pathname === '/admin';
        return location.pathname.startsWith(href);
    };

    return (
        <div className="min-h-screen bg-cp-bg-alt flex" data-testid="admin-layout">
            {/* Sidebar Desktop */}
            <aside className="hidden lg:flex flex-col w-64 admin-sidebar text-white shrink-0" data-testid="admin-sidebar">
                <div className="p-6 border-b border-white/10">
                    <Link to="/admin" className="flex items-center gap-2">
                        <PawPrint size={24} className="text-cp-primary" />
                        <span className="font-heading font-bold text-lg">CloudPet Admin</span>
                    </Link>
                </div>
                <nav className="flex-1 py-4 px-3 space-y-1">
                    {navItems.map(item => (
                        <Link
                            key={item.href}
                            to={item.href}
                            data-testid={`admin-nav-${item.label.toLowerCase()}`}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                isActive(item.href)
                                    ? 'bg-white/15 text-white border-l-[3px] border-cp-primary'
                                    : 'text-white/70 hover:text-white hover:bg-white/10'
                            }`}
                        >
                            <item.icon size={18} />
                            {item.label}
                        </Link>
                    ))}
                </nav>
                <div className="p-4 border-t border-white/10">
                    <Link to="/" className="flex items-center gap-2 px-3 py-2 text-white/60 hover:text-white text-sm transition-colors mb-2">
                        <PawPrint size={16} /> Ver tienda
                    </Link>
                    <button
                        onClick={handleLogout}
                        data-testid="admin-logout"
                        className="flex items-center gap-2 px-3 py-2 text-white/60 hover:text-white text-sm transition-colors w-full"
                    >
                        <LogOut size={16} /> Cerrar sesion
                    </button>
                </div>
            </aside>

            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} />
                    <aside className="absolute left-0 top-0 bottom-0 w-64 admin-sidebar text-white flex flex-col">
                        <div className="p-6 border-b border-white/10 flex items-center justify-between">
                            <span className="font-heading font-bold text-lg">CloudPet</span>
                            <button onClick={() => setSidebarOpen(false)} className="text-white/70 hover:text-white">
                                <X size={20} />
                            </button>
                        </div>
                        <nav className="flex-1 py-4 px-3 space-y-1">
                            {navItems.map(item => (
                                <Link
                                    key={item.href}
                                    to={item.href}
                                    onClick={() => setSidebarOpen(false)}
                                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                        isActive(item.href)
                                            ? 'bg-white/15 text-white'
                                            : 'text-white/70 hover:text-white hover:bg-white/10'
                                    }`}
                                >
                                    <item.icon size={18} />
                                    {item.label}
                                </Link>
                            ))}
                        </nav>
                        <div className="p-4 border-t border-white/10">
                            <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 text-white/60 hover:text-white text-sm w-full">
                                <LogOut size={16} /> Cerrar sesion
                            </button>
                        </div>
                    </aside>
                </div>
            )}

            {/* Main */}
            <div className="flex-1 flex flex-col min-h-screen overflow-x-hidden">
                {/* Mobile header */}
                <header className="lg:hidden bg-white border-b border-cp-border px-4 py-3 flex items-center justify-between">
                    <button onClick={() => setSidebarOpen(true)} className="text-cp-text-main" data-testid="admin-mobile-menu">
                        <Menu size={22} />
                    </button>
                    <span className="font-heading font-bold text-cp-brand">CloudPet Admin</span>
                    <div className="w-6" />
                </header>
                <main className="flex-1 p-4 lg:p-8">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
